import type { SortingState } from "@tanstack/react-table";

import { cn } from "@/lib/utils";
import {
  colWidth,
  dataTableMeta,
  type DataTableColumnMeta,
} from "@/components/ui/data-table-column-width";

export function sortAdminTableRows<T extends { id: string; sortOrder: number }>(
  items: T[],
  savedItems: T[],
) {
  const savedIndex = new Map(
    savedItems.map((item, index) => [item.id, index]),
  );

  return items
    .map((item, index) => ({ item, index }))
    .sort((left, right) => {
      const sortDiff = left.item.sortOrder - right.item.sortOrder;
      if (sortDiff !== 0) {
        return sortDiff;
      }

      const leftSaved = savedIndex.get(left.item.id);
      const rightSaved = savedIndex.get(right.item.id);

      if (leftSaved != null && rightSaved != null) {
        return leftSaved - rightSaved;
      }

      return left.index - right.index;
    })
    .map(({ item }) => item);
}

export function collectDirtyIds<T extends { id: string }>(
  formItems: T[],
  savedItems: T[],
) {
  const savedById = new Map(savedItems.map((item) => [item.id, item]));
  const dirty = new Set<string>();

  for (const formItem of formItems) {
    const savedItem = savedById.get(formItem.id);
    if (!savedItem || JSON.stringify(formItem) !== JSON.stringify(savedItem)) {
      dirty.add(formItem.id);
    }
  }

  return dirty;
}

export const ADMIN_TABLE_PAGE_SIZES = {
  links: { pageSize: 12, pageSizeOptions: [12, 20, 50, 100] },
  linkGroups: { pageSize: 12, pageSizeOptions: [12, 20, 50, 100] },
  ads: { pageSize: 12, pageSizeOptions: [12, 20, 50, 100] },
  announcements: { pageSize: 10, pageSizeOptions: [10, 20, 100] },
  log: { pageSize: 12, pageSizeOptions: [12, 50, 100, 200, 500] },
  adminLog: { pageSize: 15, pageSizeOptions: [15, 50, 100, 200, 500] },
  walletLedger: { pageSize: 15, pageSizeOptions: [15, 30, 50, 100] },
  refund: { pageSize: 12, pageSizeOptions: [12, 20, 50, 100] },
  shopOrders: { pageSize: 12, pageSizeOptions: [12, 20, 50, 100] },
  invoice: { pageSize: 12, pageSizeOptions: [12, 20, 50, 100] },
  users: { pageSize: 12, pageSizeOptions: [12, 20, 50, 100] },
  userInfo: { pageSize: 15, pageSizeOptions: [15, 30, 50, 100] },
  attachments: { pageSize: 12, pageSizeOptions: [12, 20, 50, 100] },
} as const;

export type AdminTablePagePreset = keyof typeof ADMIN_TABLE_PAGE_SIZES;

export type AdminTableSerialOrder = "asc" | "desc";

export type AdminTableSerialStrategy = "auto-stable" | "auto-page" | "field";

export type AdminTableSerialColumnMeta = {
  className?: string;
  serialStrategy?: AdminTableSerialStrategy;

  customSerial?: boolean;
  draggable?: boolean;
  /** @deprecated use serialStrategy: "auto-stable" */
  stableSerial?: boolean;
};

export type AdminTableSerialMode = "page" | "field";

export type AdminTableDataSerialBase = AdminTableSerialOrder;

export function shouldReverseTableDataForSerial(
  serialOrder: AdminTableSerialOrder,
  dataSerialBase: AdminTableDataSerialBase = "asc",
) {
  return serialOrder !== dataSerialBase;
}

export function sortTableRowsBySerialField<
  T extends { serial?: number | null },
>(rows: T[], order: AdminTableSerialOrder = "desc") {
  return [...rows].sort((left, right) => {
    const diff = (left.serial ?? 0) - (right.serial ?? 0);
    return order === "desc" ? -diff : diff;
  });
}

export function getAdminTablePageSerial(
  rowIndex: number,
  rowsOnPage: number,
  order: AdminTableSerialOrder = "desc",
) {
  return order === "desc" ? Math.max(rowsOnPage - rowIndex, 1) : rowIndex + 1;
}

export function getAdminTableStableSerial(coreIndex: number) {
  return coreIndex + 1;
}

export function columnHasSerial(
  columns: Array<{ id?: string; accessorKey?: unknown }>,
) {
  return columns.some(
    (column) =>
      column.id === "serial" ||
      String(column.accessorKey ?? "") === "serial",
  );
}

export function getAdminTableSerialColumnMeta(
  columns: Array<{ id?: string; accessorKey?: unknown; meta?: unknown }>,
): AdminTableSerialColumnMeta | null {
  const column = columns.find(
    (item) =>
      item.id === "serial" || String(item.accessorKey ?? "") === "serial",
  );

  if (!column) {
    return null;
  }

  return (column.meta ?? {}) as AdminTableSerialColumnMeta;
}

export function resolveAdminTableSerialStrategy(
  columns: Array<{ id?: string; accessorKey?: unknown; meta?: unknown }>,
  fallback: AdminTableSerialStrategy = "auto-page",
): AdminTableSerialStrategy {
  return getAdminTableSerialColumnMeta(columns)?.serialStrategy ?? fallback;
}

export function resolveAdminTableSerialMode(
  columns: Array<{ id?: string; accessorKey?: unknown; meta?: unknown }>,
  explicit?: AdminTableSerialMode,
): AdminTableSerialMode {
  if (explicit) {
    return explicit;
  }

  return resolveAdminTableSerialStrategy(columns) === "field" ? "field" : "page";
}

export function shouldPassSerialOrderToDataTable(
  columns: Array<{ id?: string; accessorKey?: unknown; meta?: unknown }>,
): boolean {
  const meta = getAdminTableSerialColumnMeta(columns);

  if (!meta || !columnHasSerial(columns)) {
    return false;
  }

  if (meta.customSerial || meta.serialStrategy === "field") {
    return false;
  }

  return true;
}

export function getAdminTablePageConfig(preset: AdminTablePagePreset) {
  return ADMIN_TABLE_PAGE_SIZES[preset];
}

export function resolveAdminTableSortParams(
  sorting: SortingState,
  fallback: SortingState,
  options: { rename?: Record<string, string>; allow?: readonly string[] } = {},
): { sortBy: string; order: "asc" | "desc" } {
  const entry = sorting[0] ?? fallback[0]!;
  const id =
    options.allow && !options.allow.includes(entry.id)
      ? (fallback[0]?.id ?? entry.id)
      : entry.id;

  return {
    sortBy: options.rename?.[id] ?? id,
    order: entry.desc ? "desc" : "asc",
  };
}

export function isSameAdminTableSorting(
  left: SortingState,
  right: SortingState,
) {
  return (
    left.length === right.length &&
    left.every(
      (entry, index) =>
        entry.id === right[index]?.id && entry.desc === right[index]?.desc,
    )
  );
}

export function shortenAdminTableText(value: string, max = 10) {
  if (!value || value === "—") {
    return "—";
  }

  return value.length > max ? `${value.slice(0, max)}...` : value;
}

export function getAdminTableSerialSorting(
  serialOrder: AdminTableSerialOrder,
) {
  return [{ id: "serial" as const, desc: serialOrder === "desc" }];
}

export function getAdminTableSortOrderSerialSorting(
  order: AdminTableSerialOrder = "asc",
) {
  return [
    { id: "sortOrder" as const, desc: order === "desc" },
    { id: "serial" as const, desc: order === "desc" },
  ];
}

export const ADMIN_TABLE_PAGE_SERIAL_DEFAULTS = {
  defaultSorting: getAdminTableSerialSorting("desc"),
  serialOrder: "desc" as AdminTableSerialOrder,
};

export const ADMIN_TABLE_SERIAL_AUTO_STABLE_PROPS =
  ADMIN_TABLE_PAGE_SERIAL_DEFAULTS;

export const ADMIN_TABLE_SORT_ORDER_SERIAL_PROPS = {
  defaultSorting: getAdminTableSortOrderSerialSorting("asc"),
  serialOrder: "asc" as AdminTableSerialOrder,
};

export const ADMIN_TABLE_SERIAL_AUTO_PAGE_PROPS =
  ADMIN_TABLE_PAGE_SERIAL_DEFAULTS;

export const ADMIN_TABLE_SERIAL_DRAGGABLE_PROPS = {
  defaultSorting: getAdminTableSerialSorting("desc"),
} as const;

export const ADMIN_TABLE_SERIAL_FIELD_PROPS = {
  serialMode: "field" as const,
  showSerialOrderSelect: false,
} as const;

export const ADMIN_TABLE_PAGINATION_DEFAULTS = {
  showPagination: true,
  showPageSizeSelector: true,
  showPageJumpInput: true,
} as const;

export function defaultCanBatchDelete(selectedIds: string[]) {
  return selectedIds.length > 0;
}

export function canBatchDeleteUnlessAll(
  selectedIds: string[],
  totalCount: number,
) {
  return selectedIds.length > 0 && totalCount > selectedIds.length;
}

export const ADMIN_TABLE_ACTIONS_INNER_CLASS =
  "flex shrink-0 items-center gap-2 [&_button]:h-7 [&_button]:w-12 [&_button]:min-w-12 [&_button]:max-w-12 [&_button]:shrink-0 [&_button]:justify-center [&_button]:px-0";

const ACTION_BUTTON_WIDTH = 48;
const ACTION_BUTTON_GAP = 8;
const ACTION_CELL_PADDING_X = 16;

const ICON_ACTION_BUTTON_SIZE = 32;
const ICON_ACTION_BUTTON_GAP = 4;

function getActionColumnWidth(buttonCount: 1 | 2 | 3 | 4) {
  const buttons =
    buttonCount * ACTION_BUTTON_WIDTH + (buttonCount - 1) * ACTION_BUTTON_GAP;
  return buttons + ACTION_CELL_PADDING_X;
}

function getIconActionColumnWidth(buttonCount: 1 | 2 | 3 | 4) {
  const buttons =
    buttonCount * ICON_ACTION_BUTTON_SIZE +
    (buttonCount - 1) * ICON_ACTION_BUTTON_GAP;
  return buttons + ACTION_CELL_PADDING_X;
}

export function adminTableSelectColumnMeta(
  className = "px-3",
): DataTableColumnMeta {
  return dataTableMeta(colWidth(40), { className });
}

export function adminTableActionsColumnMeta(
  buttonCount: 1 | 2 | 3 | 4,
  options?: {
    className?: string;
  },
): DataTableColumnMeta {
  const width = getActionColumnWidth(buttonCount);

  return dataTableMeta(colWidth(width), {
    className: cn(
      "overflow-visible align-middle whitespace-nowrap",
      options?.className,
    ),
    noTruncate: true,
  });
}

export function adminTableIconActionsColumnMeta(
  buttonCount: 1 | 2 | 3 | 4,
  options?: {
    className?: string;
  },
): DataTableColumnMeta {
  const width = getIconActionColumnWidth(buttonCount);

  return dataTableMeta(colWidth(width), {
    className: cn(
      "overflow-visible align-middle whitespace-nowrap",
      options?.className,
    ),
    noTruncate: true,
  });
}
